import React from 'react';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { Room } from '../../models/Room';

const CLEAR_ROOM_VALUE = '__clear_room__';

export interface HostTutorBookingRoomAssignmentFieldProps {
  bookingId: string;
  roomId?: string | null;
  roomName?: string | null;
  rooms: Room[];
  roomsLoading?: boolean;
  canAssign: boolean;
  busy?: boolean;
  onAssign: (bookingId: string, roomId: string) => void;
  onClear: (bookingId: string) => void;
}

const HostTutorBookingRoomAssignmentField: React.FC<HostTutorBookingRoomAssignmentFieldProps> = ({
  bookingId,
  roomId,
  roomName,
  rooms,
  roomsLoading = false,
  canAssign,
  busy = false,
  onAssign,
  onClear,
}) => {
  const { t } = useTranslation();
  const resolvedRoomId = roomId || '';
  const roomLabel = t('hostTutorBookings.roomLabel', 'Room');
  const placeholder =
    rooms.length === 0
      ? t('hostTutorBookings.noRoomsForSpace', 'No rooms in this space')
      : t('hostTutorBookings.selectRoomPlaceholder', 'Select a room');
  const displayRoomName =
    roomName?.trim() ||
    rooms.find((room) => room.id === resolvedRoomId)?.name ||
    '';

  if (!canAssign) {
    if (!displayRoomName) {
      return null;
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <MeetingRoomOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary" noWrap title={displayRoomName}>
          {displayRoomName}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75}>
      <FormControl fullWidth size="small" disabled={busy || roomsLoading || rooms.length === 0}>
        <InputLabel id={`host-tutor-booking-room-${bookingId}-label`} shrink>
          {roomLabel}
        </InputLabel>
        <Select
          labelId={`host-tutor-booking-room-${bookingId}-label`}
          label={roomLabel}
          notched
          value={resolvedRoomId}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography component="span" variant="body2" color="text.secondary">
                  {placeholder}
                </Typography>
              );
            }
            const selectedName =
              rooms.find((room) => room.id === selected)?.name || displayRoomName || selected;
            return selectedName;
          }}
          onChange={(event) => {
            const nextRoomId = String(event.target.value);
            if (nextRoomId === CLEAR_ROOM_VALUE) {
              if (resolvedRoomId) {
                onClear(bookingId);
              }
              return;
            }
            if (nextRoomId && nextRoomId !== resolvedRoomId) {
              onAssign(bookingId, nextRoomId);
            }
          }}
          IconComponent={
            roomsLoading || busy
              ? () => <CircularProgress size={14} sx={{ mr: 1.5 }} />
              : undefined
          }
        >
          {rooms.map((room) => (
            <MenuItem key={room.id} value={room.id}>
              {room.name}
            </MenuItem>
          ))}
          {resolvedRoomId ? (
            <MenuItem value={CLEAR_ROOM_VALUE} sx={{ color: 'text.secondary' }}>
              {t('hostTutorBookings.clearRoom', 'Clear room')}
            </MenuItem>
          ) : null}
        </Select>
      </FormControl>
      {!resolvedRoomId ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {t(
            'hostTutorBookings.assignRoomHint',
            'Assign a room to reserve it on the space calendar for this lesson.',
          )}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default HostTutorBookingRoomAssignmentField;
